import type {
  RoomLayoutApiResponse,
  RoomLayoutError,
  RoomLayoutSpot
} from '../components/RoomLayoutMap/RoomLayoutMap.types';
import { getCanonicalSpotName, normalizeSpot } from '../components/RoomLayoutMap/spotKey';
import { nonEmptyTrimmed } from './spotName';
import type { InteractiveSpotBookingParamsLike, SpotIdentityLike } from './types';
import { resolveIsChangeSpotFlow, resolvePreviousSpotIdForChangeSpot } from './spotBookingLogic';

export type CanonicalSpotLookup = {
  byAnyId: Record<string, string>;
  aliasesByCanonical: Record<string, string[]>;
};

export type CanonicalAliasConflict = {
  alias: string;
  existingCanonical: string;
  attemptedCanonical: string;
};

export type MergeCanonicalSpotLookupsOptions = {
  onConflict?: (conflict: CanonicalAliasConflict) => void;
};

export const emptyCanonicalSpotLookup: CanonicalSpotLookup = {
  byAnyId: {},
  aliasesByCanonical: {}
};

function normalizeAliasKey(alias: string): string {
  return alias.toLowerCase();
}

function addCanonicalAlias(
  lookup: CanonicalSpotLookup,
  alias: string,
  canonical: string
): CanonicalAliasConflict | undefined {
  if (alias === '') return undefined;
  const aliasKey = normalizeAliasKey(alias);
  const existingCanonical = lookup.byAnyId[aliasKey];
  if (existingCanonical != null && existingCanonical !== canonical) {
    return { alias, existingCanonical, attemptedCanonical: canonical };
  }
  lookup.byAnyId[aliasKey] = canonical;
  const existing = lookup.aliasesByCanonical[canonical] ?? [];
  if (!existing.includes(alias)) {
    lookup.aliasesByCanonical[canonical] = [...existing, alias];
  }
  return undefined;
}

function applyCanonicalAlias(
  lookup: CanonicalSpotLookup,
  alias: string,
  canonical: string,
  onConflict?: (conflict: CanonicalAliasConflict) => void
): void {
  const conflict = addCanonicalAlias(lookup, alias, canonical);
  if (conflict) {
    onConflict?.(conflict);
  }
}

export function buildCanonicalSpotNameLookup(data: RoomLayoutApiResponse): CanonicalSpotLookup {
  const lookup: CanonicalSpotLookup = { byAnyId: {}, aliasesByCanonical: {} };
  data.spots.forEach((spot) => {
    // Guard against runtime payloads missing spotName/spot_name despite the (non-optional) type:
    // raw String(undefined) would otherwise coerce to the literal string "undefined".
    const canonical = nonEmptyTrimmed(spot.spotName) ?? '';
    if (canonical !== '') applyCanonicalAlias(lookup, canonical, canonical);
    const label = nonEmptyTrimmed(spot.spot_name) ?? '';
    if (label !== '') applyCanonicalAlias(lookup, label, canonical);
  });
  return lookup;
}

export function mergeCanonicalSpotLookups(
  lookups: CanonicalSpotLookup[],
  options?: MergeCanonicalSpotLookupsOptions
): CanonicalSpotLookup {
  const merged: CanonicalSpotLookup = { byAnyId: {}, aliasesByCanonical: {} };
  const onConflict = options?.onConflict;
  lookups.forEach((lookup) => {
    Object.entries(lookup.byAnyId).forEach(([alias, canonical]) => {
      applyCanonicalAlias(merged, alias, canonical, onConflict);
    });
  });
  return merged;
}

function toSpotId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return nonEmptyTrimmed(value);
}

function getSpotIdentityKeys(spot: SpotIdentityLike): string[] {
  const apiSpotId = toSpotId(spot.spot_id ?? spot.id);
  const displayLabel = nonEmptyTrimmed(spot.spot_name) ?? nonEmptyTrimmed(spot.spotName);
  const canonical = apiSpotId ?? nonEmptyTrimmed(spot.spotName) ?? nonEmptyTrimmed(spot.spot_name);
  return [canonical, apiSpotId, displayLabel].filter(
    (key): key is string => typeof key === 'string' && key.length > 0
  );
}

export function buildCanonicalSpotNameLookupFromEventSpots(
  spots: SpotIdentityLike[] | undefined
): CanonicalSpotLookup {
  const lookup: CanonicalSpotLookup = { byAnyId: {}, aliasesByCanonical: {} };
  (spots ?? []).forEach((spot) => {
    const keys = getSpotIdentityKeys(spot);
    const canonical = keys[0];
    if (!canonical) {
      return;
    }
    keys.forEach((key) => {
      applyCanonicalAlias(lookup, key, canonical);
    });
  });
  return lookup;
}

export function buildEffectiveReservedSpotKeys(
  reservedSpotIdsForMap: string[],
  lookup: CanonicalSpotLookup
): string[] {
  const keys = new Set<string>();
  reservedSpotIdsForMap.forEach((id) => {
    const raw = String(id);
    if (raw !== '') keys.add(raw);
    const translated = lookup.byAnyId[normalizeAliasKey(raw)];
    if (translated) keys.add(translated);
  });
  return Array.from(keys);
}

/** Map display/API aliases to the layout grid key once the room layout has loaded. */
export function resolveCanonicalSpotKey(
  spotKey: string | null | undefined,
  lookup: CanonicalSpotLookup
): string | null {
  if (spotKey == null) return null;
  const raw = String(spotKey).trim();
  if (raw === '') return null;
  return lookup.byAnyId[normalizeAliasKey(raw)] ?? raw;
}

/** Include every alias that resolves to the same canonical spot for reserved-key filtering. */
export function expandSpotKeysWithCanonicalAliases(
  spotKeys: string[],
  lookup: CanonicalSpotLookup
): string[] {
  const result = new Set<string>();
  const canonicalTargets = new Set<string>();

  spotKeys.forEach((key) => {
    const raw = String(key).trim();
    if (raw === '') return;
    result.add(raw);
    const canonical = lookup.byAnyId[normalizeAliasKey(raw)] ?? raw;
    result.add(canonical);
    canonicalTargets.add(canonical);
  });

  canonicalTargets.forEach((canonical) => {
    const aliases = lookup.aliasesByCanonical[canonical] ?? [canonical];
    aliases.forEach((alias) => {
      result.add(alias);
    });
  });

  return Array.from(result);
}

export function getReservedSpotKeysKey(effectiveReservedSpotKeys: string[]): string {
  return JSON.stringify([...effectiveReservedSpotKeys].sort());
}

export function getRoomLayoutScopeKeys(
  layoutId: string,
  timeSlotId: string | undefined,
  reservedSpotKeysKey: string
): { roomLayoutMapKey: string; roomLayoutAvailabilityScopeKey: string } {
  const timeSlotSegment = timeSlotId ?? '';
  return {
    roomLayoutMapKey: `${layoutId}:${timeSlotSegment}:${reservedSpotKeysKey}`,
    roomLayoutAvailabilityScopeKey: `${layoutId}:${timeSlotSegment}`
  };
}

export function getSelectedSpotKeyForMap(selectedSpot: RoomLayoutSpot | null): string | null {
  if (!selectedSpot) return null;
  return getCanonicalSpotName(normalizeSpot(selectedSpot)) ?? null;
}

export function shouldShowNoSpotsAvailableWarning(
  hasBookableSpots: boolean | null,
  layoutError: RoomLayoutError | null | undefined
): boolean {
  return hasBookableSpots === false && layoutError == null;
}

export function getAssignedSpotKeyForChangeSpot(
  params: InteractiveSpotBookingParamsLike,
  isChangeSpotFlowOverride?: boolean,
  previousSpotIdOverride?: string
): string | null {
  if (!resolveIsChangeSpotFlow(params, isChangeSpotFlowOverride)) {
    return null;
  }
  const key = resolvePreviousSpotIdForChangeSpot(params, previousSpotIdOverride);
  return key !== '' ? key : null;
}

export function getAssignedSpotKeysForChangeSpot(
  params: InteractiveSpotBookingParamsLike,
  spots: SpotIdentityLike[] | undefined,
  lookup: CanonicalSpotLookup = emptyCanonicalSpotLookup,
  isChangeSpotFlowOverride?: boolean,
  previousSpotIdOverride?: string
): string[] {
  const assignedSpotKey = getAssignedSpotKeyForChangeSpot(
    params,
    isChangeSpotFlowOverride,
    previousSpotIdOverride
  );
  if (!assignedSpotKey) {
    return [];
  }
  const assignedSet = new Set<string>([assignedSpotKey]);
  const assignedLower = assignedSpotKey.toLowerCase();

  (spots ?? []).forEach((spot) => {
    const keys = getSpotIdentityKeys(spot);
    const matchesAssigned = keys.some(
      (key) => key === assignedSpotKey || key.toLowerCase() === assignedLower
    );
    if (matchesAssigned) {
      keys.forEach((key) => assignedSet.add(key));
    }
  });

  return expandSpotKeysWithCanonicalAliases(Array.from(assignedSet), lookup);
}

/**
 * Member's assignment must not be treated as a reserved/booked spot on the map (AC1–AC2).
 */
export function filterReservedSpotKeysForChangeSpot(
  reservedSpotKeys: string[],
  assignedSpotKeys: string[]
): string[] {
  if (assignedSpotKeys.length === 0) {
    return reservedSpotKeys;
  }
  const assignedSet = new Set(assignedSpotKeys.map((key) => key.toLowerCase()));
  return reservedSpotKeys.filter((key) => !assignedSet.has(key.toLowerCase()));
}
